import { Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { DateTimeFormat, DateTimeService } from '@y42/date-time';
import { ErrorHandlerService } from '@y42/error-handling';
import { ColumnTypeEnum } from '@y42/json-schema';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject, map } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'y42-cell-type-renderer',
  templateUrl: './cell-type-renderer.component.html',
  styleUrls: ['./cell-type-renderer.component.scss'],
})
export class CellTypeRendererComponent implements ICellRendererAngularComp {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}
  private readonly params$$ = new BehaviorSubject<ICellRendererParams | null>(null);

  protected readonly state = connectState(this, {
    isValueNull: this.params$$.pipe(map((params) => params?.value === null)),
    formattedValue: this.params$$.pipe(
      map((params) => {
        if (params === null) {
          return '';
        }

        const cellValue = params?.value;

        if (cellValue === null) {
          return 'NULL';
        }

        const cellValStringify = (obj: unknown) => {
          if (typeof obj === 'object') {
            return JSON.stringify(obj);
          }
          return `${obj}`;
        };

        if (cellValue instanceof Array) {
          return cellValue.map((value) => cellValStringify(value)).join(' | ');
        }

        const columnType = params.colDef?.cellRendererParams?.dataColumn?.data_type;
        if (columnType === ColumnTypeEnum.Date) {
          return this.dateFormatter(cellValue as Date);
        } else if ([ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(columnType)) {
          return this.dateTimeFormatter(cellValue as Date);
        }

        return cellValue !== undefined ? cellValStringify(cellValue) : '';
      }),
    ),
  });

  agInit(params: ICellRendererParams): void {
    this.params$$.next(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.params$$.next(params);
    return true;
  }

  private dateFormatter(dateValue?: Date) {
    if (dateValue) {
      try {
        return this.dateTimeService.formatDate(dateValue, DateTimeFormat.ShortDate);
      } catch (err) {
        this.errorHandlerService.handleError(err);
        return 'Invalid date';
      }
    }
    return '';
  }

  private dateTimeFormatter(dateValue?: Date) {
    if (dateValue) {
      try {
        return new Date(dateValue).toISOString();
      } catch (err) {
        this.errorHandlerService.handleError(err);
        return 'Invalid datetime';
      }
    }
    return '';
  }
}
